import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

export const primaryCategorySchema = z.union([
  z.literal("growin-stock-exploration"),
  z.literal("market-reports"),
  z.literal("investment-knowledge"),
]);

export const secondaryCategorySchema = z.union([
  z.literal("user-guide"),
  z.literal("13f-report"),
  z.literal("market-analysis"),
  z.literal("weekly-market-analysis"),
  z.literal("us-stock-after-hours"),
  z.literal("industry-trends"),
  z.literal("earnings-report"),
  z.literal("stock-analysis"),
  z.literal("overall-market"),
  z.literal("investment-strategy"),
  z.literal("investment-psychology"),
  z.literal("quantitative-analysis"),
]);

export const contentSchema = z
  .object({
    time: z.number().nullish(),
    blocks: z
      .array(
        z.object({
          id: z.string().nullish(),
          data: z.any().nullish(),
          type: z.string().nullish(),
          tunes: z.record(z.any()).nullish(),
        })
      )
      .nullish(),
    version: z.string().nullish(),
  })
  .optional();

const responseSchema = z.object({
  data: z.array(
    z.object({
      articleId: z.string(),
      primaryCategory: primaryCategorySchema,
      secondaryCategory: secondaryCategorySchema,
      title: z.string().nullish(),
      slug: z.string().nullish(),
      coverPicture: z.string().nullish(),
      previewPicture: z.string().nullish(),
      description: z.string().nullish(),
      keywords: z.array(z.string()).nullish(),
      author: z.string().nullish(),
      content: contentSchema,
      permission: z.union([z.literal("Free"), z.literal("Subscribe")]),
      publishedAt: z.string().nullish(),
    })
  ),
  message: z.string(),
});

export type Content = z.infer<typeof contentSchema>;
export type ArticleListData = z.infer<typeof responseSchema>["data"];

type ArticleListParams = {
  lang: Lang;
  page?: number;
  pageSize?: number;
};

export const getArticleList = async ({
  lang,
  page,
  pageSize,
}: ArticleListParams): Promise<ArticleListData> => {
  const url = new URL(`${NIKE_URL}/article-zone`);
  if (page) url.searchParams.append("page", page.toString());
  if (pageSize) url.searchParams.append("pageSize", pageSize.toString());
  const endpoint = url.toString();
  const res = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Accept-Language": lang,
    },
    cache: "no-cache",
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseSchema.parse(await res.json()).data;
};

export const useArticleList = ({
  page,
  pageSize,
}: Omit<ArticleListParams, "lang">) => {
  const { lang } = useLang();
  return useQuery(["article-list", page, pageSize, lang], () =>
    getArticleList({ page, pageSize, lang })
  );
};
